<script setup lang="ts">
import { computed } from 'vue';
import aiSourcedSparkleUrl from '@/assets/svg/ai-sourced-sparkle.svg';
import atsSyncUrl from '@/assets/svg/ats-sync.svg';
import FactoryFixMark from '@/assets/svg/ff-logo-mono.svg?component';
import SourcedUserSearchIcon from '@/assets/svg/sourced-user-search.svg?component';
import { getIntegrationDisplayName } from '@/core/conversations/conversation-index/ats-source.util';

interface Props {
  integrationId?: string | null;
  isAiSourced?: boolean;
  /** Recruiter-initiated outreach (Talent Search add, Contact as Prospects), not an application. */
  isSourced?: boolean;
  /** Show the icon only, with the label kept for screen readers and a tooltip on hover. */
  iconOnly?: boolean;
  /** Taller pill (1.5rem) for the candidate card; every other usage stays 1.25rem. */
  tall?: boolean;
  /** 700 label for the candidate card; every other usage stays 600. */
  bold?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isAiSourced: false,
  isSourced: false,
  iconOnly: false,
  tall: false,
  bold: false,
});

defineOptions({ inheritAttrs: false });

const atsName = computed(() => getIntegrationDisplayName(props.integrationId));

const origin = computed(() => {
  if (atsName.value) {
    return {
      testId: 'applicant-origin-chip-ats',
      label: atsName.value,
      containerClass: 'bg-inform-50 text-inform-1000',
    };
  }

  if (props.isAiSourced) {
    return {
      testId: 'applicant-origin-chip-ai-sourced',
      label: 'AI Sourced',
      containerClass: 'bg-gradient-to-r from-copilot-500/10 to-highlight-600/10 text-highlight-600',
    };
  }

  if (props.isSourced) {
    return {
      testId: 'applicant-origin-chip-sourced',
      label: 'Sourced',
      containerClass: 'bg-primary-50 text-primary-700',
    };
  }

  return {
    testId: 'applicant-origin-chip-native',
    label: 'Applicant',
    containerClass: 'bg-tint-40 text-shade-840',
  };
});
</script>

<template>
  <div
    v-bind="$attrs"
    :data-test-id="origin.testId"
    class="badge-container gap-1"
    :class="[origin.containerClass, { 'badge-container--tall': tall }]"
  >
    <img
      v-if="atsName"
      :src="atsSyncUrl"
      class="h-3 w-3 shrink-0"
      alt=""
      aria-hidden="true"
      data-test-id="applicant-origin-chip-icon"
    />
    <img
      v-else-if="isAiSourced"
      :src="aiSourcedSparkleUrl"
      class="h-3 w-3 shrink-0"
      alt=""
      aria-hidden="true"
      data-test-id="applicant-origin-chip-icon"
    />
    <SourcedUserSearchIcon
      v-else-if="isSourced"
      class="h-3 w-3 shrink-0"
      aria-hidden="true"
      data-test-id="applicant-origin-chip-icon"
    />
    <FactoryFixMark
      v-else
      class="h-3 w-4 shrink-0"
      aria-hidden="true"
      data-test-id="applicant-origin-chip-icon"
    />
    <span
      :class="
        iconOnly
          ? 'sr-only'
          : ['whitespace-nowrap text-xs leading-[1.5]', bold ? 'font-bold' : 'font-semibold']
      "
      >{{ origin.label }}</span
    >
    <v-tooltip
      v-if="iconOnly"
      activator="parent"
      location="bottom"
      content-class="application-picker__tooltip"
    >
      {{ origin.label }}
    </v-tooltip>
  </div>
</template>

<style lang="postcss" scoped>
.badge-container {
  @apply flex h-5 items-center justify-center rounded-xl p-1.5;
}
.badge-container--tall {
  @apply h-6;
}
</style>
